import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { getCacheConfig } from '../config';
import { persistQueryCache, restoreQueryCache } from '../utils/queryPersistence';

const cacheConfig = getCacheConfig();

const createRetryDelay = (attemptIndex: number): number =>
  Math.min(1000 * 2 ** attemptIndex, 30000);

const createQueryDefaults = () => ({
  staleTime: cacheConfig.queryCache.staleTimeMs,
  gcTime: cacheConfig.queryCache.gcTimeMs,
  retry: 3,
  retryDelay: createRetryDelay,
  refetchOnWindowFocus: false,
  refetchOnMount: false,
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: createQueryDefaults(),
  },
});

restoreQueryCache(queryClient);

const persistCache = (): void => {
  persistQueryCache(queryClient);
};

const setupPeriodicPersistence = (): (() => void) => {
  const interval = setInterval(persistCache, cacheConfig.queryCache.persistIntervalMs);
  return () => clearInterval(interval);
};

const setupUnloadPersistence = (): (() => void) => {
  const handleBeforeUnload = persistCache;
  window.addEventListener('beforeunload', handleBeforeUnload);
  return () => window.removeEventListener('beforeunload', handleBeforeUnload);
};

const setupVisibilityPersistence = (): (() => void) => {
  const handleVisibilityChange = (): void => {
    if (document.hidden) {
      persistCache();
    }
  };

  document.addEventListener('visibilitychange', handleVisibilityChange);
  return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
};

const setupPersistence = (): (() => void) => {
  const cleanupPeriodic = setupPeriodicPersistence();
  const cleanupUnload = setupUnloadPersistence();
  const cleanupVisibility = setupVisibilityPersistence();

  return () => {
    cleanupPeriodic();
    cleanupUnload();
    cleanupVisibility();
  };
};

interface QueryProviderProps {
  children: ReactNode;
}

const shouldShowDevTools = (): boolean => import.meta.env.DEV;

const renderDevTools = () => (
  <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" position="bottom" />
);

export const QueryProvider = ({ children }: QueryProviderProps) => {
  useEffect(() => {
    const cleanup = setupPersistence();
    return cleanup;
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {shouldShowDevTools() && renderDevTools()}
    </QueryClientProvider>
  );
};

export { queryClient };
